import {
  Stack,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import { Button, Card } from '@repo/ui'
import { chartSeriesColor } from 'components/analytics/chartStyle'
import { Progress } from 'components/display/Progress/Progress'
import { useCanAccess } from 'hooks'
import { useRunSeoJob } from 'hooks/api'
import type { SeoJob, SeoStatus } from 'services'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, dateTime, useTone } from '../shared'
import { fmt, fmtScheduled, fmtUsd, scheduleLabel, sourceText, zoneLabel } from './seoModel'

/**
 * Cadence text per job. Rankings and AI answers read the slot from the API's settings
 * (VRG-676), so the table cannot disagree with what the scheduler actually runs.
 */
const JOBS: ReadonlyArray<{ job: SeoJob; label: string; cadence: (s: SeoStatus) => string }> = [
  { job: 'gsc-sync', label: 'Search Console sync', cadence: () => 'nightly' },
  {
    job: 'rankings',
    label: 'Rankings',
    cadence: (s) => `${scheduleLabel(s.schedule, s.schedule.intervalDays)}, every market`,
  },
  { job: 'keyword-metrics', label: 'Keyword metrics', cadence: () => 'monthly' },
  { job: 'gap-remine', label: 'Competitor gap re-mine', cadence: () => 'monthly, Direct, top 20, capped' },
  { job: 'master-rescore', label: 'Master re-score', cadence: () => 'monthly, after keyword metrics' },
  {
    job: 'aeo-runner',
    label: 'AI answers',
    cadence: (s) => scheduleLabel(s.schedule, s.schedule.aeoIntervalDays),
  },
  {
    job: 'ai-search',
    label: 'SE Ranking AI search',
    // Claimed per calendar month (seo-aeo.service.ts runAiSearch → monthKey); VRG-667.
    cadence: () => 'monthly, off by default',
  },
]

const Meter = ({
  label,
  used,
  cap,
  format,
}: {
  label: string
  used: number
  cap: number
  format: (n: number) => string
}) => {
  const tone = useTone()
  const theme = useTheme()
  const pct = cap > 0 ? Math.min((used / cap) * 100, 100) : 0
  // VERA tones, not LinearProgress `color="error" | "warning"` — those read MUI's default
  // red/orange, which the VERA theme does not define. Under 70% it is the house series
  // colour, the purple every chart on this page draws with.
  const bar =
    pct >= 90
      ? tone('negative').color
      : pct >= 70
        ? tone('caution').color
        : chartSeriesColor(theme)
  return (
    <Stack spacing={1} sx={{ minWidth: 220, flex: 1 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body3" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body3">
          {format(used)} of {format(cap)}
        </Typography>
      </Stack>
      {/* The shared `Progress`, for its mode-aware track. `color` keeps one flat fill and
          one height at every threshold; its gradient default would change both at 70%. */}
      <Progress value={pct} min={0} max={100} color={bar} hideLabels />
    </Stack>
  )
}

/**
 * What the tab is running on, and what it costs. Spend is Vera's own ledger
 * (`seo_job_runs`) against the monthly caps every job checks BEFORE spending; the balance
 * is SE Ranking's, read live on a free call. They differ when another tool shares the key.
 */
export const SeoStatusCard = ({ status }: { status: SeoStatus }) => {
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const run = useRunSeoJob()
  // Under the log driver every source is "not configured" for the same reason, which the
  // driver notice already says once; repeating it per source is noise.
  const notices =
    status.driver === 'log'
      ? []
      : [
          sourceText('SE Ranking', status.sources.seranking),
          sourceText('Live SERP', status.sources.serp),
          sourceText('Search Console', status.sources.gsc),
        ].filter((t): t is string => !!t)

  return (
    <Card title="Jobs and spend">
      <Stack spacing={4} sx={{ mt: 4 }}>
        {status.driver === 'log' ? (
          <Notice severity="info">
            This API runs the SEO log driver: jobs record what they would do and call no vendor.
            Figures below are whatever earlier runs or imports left in the database.
          </Notice>
        ) : null}
        {!status.jobsEnabled ? (
          <Notice severity="warning">
            Scheduled jobs are off on this API. Nothing refreshes on its own; a platform admin can
            still run a job by hand below.
          </Notice>
        ) : null}
        {notices.map((n) => (
          <Notice key={n} severity="warning">
            {n}
          </Notice>
        ))}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={6}>
          <Meter
            label={`SE Ranking credits, ${status.spend.month}`}
            used={status.spend.credits}
            cap={status.spend.creditCap}
            format={(n) => fmt(n)}
          />
          <Meter
            label={`LLM spend, ${status.spend.month}`}
            used={status.spend.costUsd}
            cap={status.spend.costCapUsd}
            format={fmtUsd}
          />
        </Stack>
        <SectionNote>
          Spend is Vera&rsquo;s own ledger. Each call is checked against the cap at its estimated
          cost before it is made, so a job that would cross a cap stops short of it. Rankings and
          keyword metrics then record what SE Ranking actually charged; other jobs keep the
          estimate (marked est.). SE Ranking balance:{' '}
          {status.balance?.unitsLeft !== null && status.balance?.unitsLeft !== undefined
            ? `${fmt(status.balance.unitsLeft)} of ${fmt(status.balance.unitsLimit)} credits left${
                status.balance.expiresAt
                  ? `, plan ends ${status.balance.expiresAt.slice(0, 10)}`
                  : ''
              }`
            : 'not available'}
          . The balance is account-wide, so other tools using the same key move it too. Live SERP:{' '}
          {status.serp.provider} ({status.serp.method}), about {status.serp.creditsPerQuery} credits
          a query.
        </SectionNote>

        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell>Job</HeadCell>
              <HeadCell>Cadence</HeadCell>
              <HeadCell>Last run</HeadCell>
              <HeadCell>Next run (New York)</HeadCell>
              <HeadCell>Status</HeadCell>
              <HeadCell align="right">Credits</HeadCell>
              <HeadCell align="right">Cost</HeadCell>
              <HeadCell align="right"> </HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {JOBS.map(({ job, label, cadence }) => {
              const last = status.runs.find((r) => r.job === job)
              const pending = run.isPending && run.variables?.job === job
              return (
                <TableRow key={job}>
                  <Cell strong>{label}</Cell>
                  <Cell>{cadence(status)}</Cell>
                  <Cell>{last ? dateTime(last.finishedAt ?? last.claimedAt) : 'never'}</Cell>
                  <Cell>
                    {status.jobsEnabled
                      ? fmtScheduled(status.schedule.nextRuns[job], status.schedule.timeZone)
                      : 'off'}
                  </Cell>
                  <Cell>
                    {last
                      ? last.error
                        ? `${last.status}: ${last.error.slice(0, 60)}`
                        : last.status
                      : '—'}
                  </Cell>
                  <Cell align="right">
                    {last
                      ? `${fmt(last.creditsSpent)}${
                          last.creditsBasis === 'estimated' && last.creditsSpent > 0 ? ' est.' : ''
                        }`
                      : '—'}
                  </Cell>
                  <Cell align="right">{last ? fmtUsd(last.costUsd) : '—'}</Cell>
                  <Cell align="right">
                    <Button
                      variant="secondary"
                      disabled={!isAdmin || run.isPending}
                      disabledReason={
                        !isAdmin ? 'Only platform admins can run SEO jobs.' : undefined
                      }
                      loading={pending}
                      onClick={() => run.mutate({ job })}
                    >
                      Run now
                    </Button>
                  </Cell>
                </TableRow>
              )
            })}
            {JOBS.length === 0 ? <EmptyRow colSpan={8} text="No jobs." /> : null}
          </TableBody>
        </StatTable>
        {run.data ? (
          <SectionNote>
            {run.data.job}:{' '}
            {run.data.claimed
              ? run.data.status
              : run.data.status === 'already_done'
                ? 'not claimed (already ran this period)'
                : `not run (${run.data.status.replace(/_/g, ' ')})`}
            {run.data.error ? ` — ${run.data.error}` : ''}
          </SectionNote>
        ) : null}
        {run.error ? (
          <Notice severity="error">
            {(run.error as Error).message}
          </Notice>
        ) : null}
        <SectionNote>
          Rankings in every market and AI answers run together,{' '}
          {scheduleLabel(status.schedule, status.schedule.intervalDays)} (next runs are shown in{' '}
          {zoneLabel(status.schedule.timeZone)}). If the API is down at that time, the run starts
          as soon as it is back the same day; a day missed entirely is skipped, not caught up.
          The review follows once the US and UK results are in. <em>Run now</em> runs a job by
          hand and does not move the schedule. A run claims its period, so pressing it again in
          the same period does nothing; a failed run can be retried. Rankings follow the tracking rule: approved terms with buyer fit
          &ge; {status.settings.trackRule.minBuyerFit} and &ge; {status.settings.trackRule.minVolume}{' '}
          monthly searches, plus own-it terms and each market&rsquo;s explicit set.{' '}
          {fmt(status.counts.keywords)} keywords in the vocabulary.
        </SectionNote>
      </Stack>
    </Card>
  )
}
